import { createContext, useContext, ReactNode } from 'react';
import type { PermissionId } from '@revont/shared-types';

/**
 * PermissionsContext
 *
 * A provider-backed context for sharing the host app's permission check
 * function with shared-ui components (notably `PermissionButton`).
 *
 * The shared-ui package cannot depend on app-level hooks, so apps pass
 * a `hasPermission(id: PermissionId) => boolean` function down via the provider.
 *
 * Default value (no provider): permissive - returns true for all ids so
 * Storybook stories and tests that do not wrap in a provider are unaffected.
 */

export type PermissionsRole = 'admin' | 'sales-rep' | null;

interface PermissionsContextValue {
  hasPermission: (permissionId: PermissionId) => boolean;
  role: PermissionsRole;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  hasPermission: () => true,
  role: null,
});

type PermissionsProviderProps = {
  hasPermission: (permissionId: PermissionId) => boolean;
  role: PermissionsRole;
  children: ReactNode;
};

export const PermissionsProvider = ({ hasPermission, role, children }: PermissionsProviderProps) => {
  return (
    <PermissionsContext.Provider value={{ hasPermission, role }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const useSharedPermissions = (): PermissionsContextValue => {
  return useContext(PermissionsContext);
};

export { PermissionsContext };
